// Shared helpers used across the dashboard. Mirrors stratum-analytical's
// `utils/` + `helpers.ts` conventions.
import { useState, useEffect, useCallback } from 'react';
import { CHECKLIST_STEPS } from '../data';

export const isDone = (appt) => appt.checks.every(Boolean);

export function getNextStep(appt) {
  for (let i = 0; i < CHECKLIST_STEPS.length; i++) {
    const step = CHECKLIST_STEPS[i];
    if (step.toLowerCase() === 'photos') continue;
    if (!appt.checks[i]) return { step, idx: i };
  }
  return null;
}

export function getPhotoIdentity(appt) {
  return {
    idBooking: appt.externalId || appt.id,
    customerName: appt.car || appt.customer || 'Unknown',
    serviceLabel: appt.service || 'Detail Service',
  };
}

export function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function formatDisplayDate(dateStr) {
  const d         = new Date(dateStr + 'T12:00:00');
  const today     = toDateStr(new Date());
  const tomorrow  = toDateStr(new Date(Date.now() + 86400000));
  const yesterday = toDateStr(new Date(Date.now() - 86400000));
  if (dateStr === today)     return 'Today';
  if (dateStr === tomorrow)  return 'Tomorrow';
  if (dateStr === yesterday) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// Light/dark theme preference, persisted to localStorage under `lightMode` and
// shared across pages (dashboard + staff). `toggleLightMode` accepts a value or
// a functional updater, matching the dashboard's existing usage.
export function useLightMode() {
  const [lightMode, setLightMode] = useState(() => {
    try {
      const raw = localStorage.getItem('lightMode');
      if (!raw) return false;
      const { value } = JSON.parse(raw);
      return value;
    } catch { return false; }
  });

  const toggleLightMode = useCallback((updater) => {
    setLightMode(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try { localStorage.setItem('lightMode', JSON.stringify({ value: next })); } catch {}
      return next;
    });
  }, []);

  return [lightMode, toggleLightMode];
}

// Whether money is shown as quoted (pre-tax) or with the location's tax added.
// Stored like lightMode: a display preference, remembered per browser, never
// sent to the server — the stored figures are always the pre-tax quote.
export function useTaxMode() {
  const [taxMode, setTaxMode] = useState(() => {
    try {
      const raw = localStorage.getItem('taxMode');
      return raw === 'after' ? 'after' : 'before';
    } catch { return 'before'; }
  });

  const chooseTaxMode = useCallback((next) => {
    setTaxMode(() => {
      const value = next === 'after' ? 'after' : 'before';
      try { localStorage.setItem('taxMode', value); } catch {}
      return value;
    });
  }, []);

  return [taxMode, chooseTaxMode];
}

// The pre-tax share of money that actually arrived. `displayCents` adds tax on
// to a quote; this takes it back off a payment, so the two sides of a card can
// be read on one basis whichever way the header switch is set.
//
// Returns the payment untouched when the location has no rate on file, rather
// than guessing one and quietly shrinking the figure.
export function collectedOnBasis(cents, rate, taxMode) {
  if (cents == null) return null;
  if (taxMode !== 'before' || !rate) return cents;
  return Math.round(cents / (1 + rate));
}

// Quoted price as it should be displayed, where the tax figure is already
// known. Preferred over `displayCents` wherever a row carries its own tax:
// the server worked it out from the booking's own location, so this cannot
// drift from what the drawer and the reports say.
//
// Only ever applied to a *quote*. Money that actually changed hands already has
// tax inside it, and adding it again would overstate what came in.
export function shownQuote(quotedCents, taxCents, taxMode) {
  if (quotedCents == null) return null;
  return taxMode === 'after' ? quotedCents + (taxCents || 0) : quotedCents;
}

// Quoted price as it should be displayed. `rate` is the location's tax rate as
// a fraction (0.13 for Ontario); null when the location has none on file, in
// which case the quote is shown untouched rather than guessed at.
export function displayCents(quotedCents, rate, taxMode) {
  if (quotedCents == null) return null;
  if (taxMode !== 'after' || rate == null) return quotedCents;
  return Math.round(quotedCents * (1 + rate));
}

export function usePagination(items, pageSize = 10) {
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const start = (safePage - 1) * pageSize;
  const pageItems = items.slice(start, start + pageSize);
  const goToPage = useCallback((p) => setPage(Math.max(1, p)), []);
  return { page: safePage, pageCount, pageItems, setPage: goToPage };
}

export function useClock() {
  const [time, setTime] = useState('');
  useEffect(() => {
    const tick = () => setTime(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return time;
}

// Split `cents` into `n` whole-cent shares that sum back to exactly `cents`.
// A $46.87 tip across 2 crew is 23.44 + 23.43 — the odd cent goes to the first
// share rather than being rounded away, so payroll always reconciles.
export function splitCents(cents, n) {
  const total = Math.round(Number(cents) || 0);
  const parts = Math.max(0, Math.floor(Number(n) || 0));
  if (!parts) return [];
  const base = Math.floor(total / parts);
  let remainder = total - base * parts;
  return Array.from({ length: parts }, () => base + (remainder-- > 0 ? 1 : 0));
}

// Today's tip earnings per staff member, keyed by their Square id. Each
// appointment's tip is shared evenly by whoever is assigned to that car.
export function tipsByStaff(appointmentsByLocation) {
  const totals = new Map();
  for (const loc of Object.values(appointmentsByLocation || {})) {
    for (const appt of loc?.appointments || []) {
      const tip = appt?.payment?.tipCents || 0;
      const crew = appt?.assignedStaff || [];
      if (!tip || !crew.length) continue;
      const shares = splitCents(tip, crew.length);
      crew.forEach((id, i) => totals.set(id, (totals.get(id) || 0) + shares[i]));
    }
  }
  return totals;
}

// Where to file a staff member in the sidebar: the location they are actually
// working today if we know it, otherwise their home location from Square.
export function staffLocation(member, appointmentsByLocation) {
  for (const [locId, loc] of Object.entries(appointmentsByLocation || {})) {
    const working = (loc?.appointments || []).some(a => (a.assignedStaff || []).includes(member.externalId));
    if (working) return locId;
  }
  return member.locationIds?.[0] || null;
}

// Quoted value of a booking in cents. Prefers the numeric field; falls back to
// parsing the display string for appointments served straight from Square.
export function apptCents(appt) {
  if (appt?.totalCents != null) return Number(appt.totalCents);
  const n = parseFloat(String(appt?.totalAmount ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

// The day's money, for the bar under the header. Everything is derived from the
// appointments already on screen so the bar can never disagree with the cards.
//
//   collected   money actually in, service revenue only (tax and tip excluded —
//               `collectedCents` is the service amount; tax and tip are separate)
//   receivables invoiced or billed to a property and not yet settled
//   due         quoted value of cars with nothing recorded against them at all
//   tips        every tip on the day, however it was taken
export function dayTotals(appointmentsByLocation) {
  const t = {
    cars: 0, finished: 0,
    collectedCents: 0, collectedCars: 0,
    receivablesCents: 0, receivableKinds: new Set(),
    dueCents: 0, dueCars: 0,
    tipsCents: 0,
  };

  for (const loc of Object.values(appointmentsByLocation || {})) {
    for (const appt of loc?.appointments || []) {
      t.cars += 1;
      if (isDone(appt)) t.finished += 1;

      const p = appt.payment;
      if (!p) {
        // Nothing recorded — the car still owes its quote.
        t.dueCents += apptCents(appt);
        t.dueCars += 1;
        continue;
      }

      t.tipsCents += p.tipCents || 0;

      if (p.handling === 'receivable' && p.outstanding) {
        t.receivablesCents += p.collectedCents || 0;
        if (p.methodCode === 'hotel_account') t.receivableKinds.add('hotel');
        else t.receivableKinds.add('invoice');
      } else if (p.handling === 'action_required') {
        // A ghost is an exception, not money in — it stays owed.
        t.dueCents += apptCents(appt);
        t.dueCars += 1;
      } else if (p.collectedCents) {
        t.collectedCents += p.collectedCents;
        t.collectedCars += 1;
      }
    }
  }

  return { ...t, receivableKinds: [...t.receivableKinds] };
}

// What a booking is actually owed. The quote is the pre-tax subtotal and tax is
// charged on top, so the customer pays quoted + tax — a $179.00 job at 13%
// comes to $202.27. Returns taxCents null when the shop has no rate configured,
// so callers can show "—" rather than inventing a figure.
export function quoteBreakdown(appt) {
  const subtotalCents = apptCents(appt);
  const taxRate = appt?.taxRate ?? null;
  const taxCents = taxRate == null ? null : Math.round(subtotalCents * taxRate);
  return { subtotalCents, taxRate, taxCents, dueCents: subtotalCents + (taxCents || 0) };
}

// The nearest ancestor (or self) carrying a given class, compared directly
// rather than through a selector.
//
// `closest()` cannot be used with a CSS Modules class: the hashes include
// characters like `+` — `staff_page__9frG+` — which are not valid in a
// selector, so `closest()` throws a SyntaxError instead of returning null.
export function closestWithClass(el, className) {
  if (!className) return null;
  for (let node = el; node; node = node.parentElement) {
    if (node.classList?.contains(className)) return node;
  }
  return null;
}
